<template>
  <div v-if="isAddNewUserSidebarActive" class="uln-overlay" @click.self="closeSidebar">
    <div class="uln-sidebar">

      <!-- Header -->
      <div class="uln-header">
        <div class="uln-header-icon" :class="isEditing ? 'uln-icon-edit' : 'uln-icon-add'">
          <svg v-if="isEditing" width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <svg v-else width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
            <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <div>
          <h3 class="uln-title">{{ isEditing ? 'Editar Usuário' : 'Novo Usuário' }}</h3>
          <p class="uln-subtitle">{{ isEditing ? 'Atualize as informações do usuário' : 'Preencha os dados para criar a conta' }}</p>
        </div>
        <button class="uln-close" @click="closeSidebar">
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <!-- Avatar Preview -->
      <div class="uln-avatar-section">
        <div class="uln-avatar-preview" :style="{ background: getAvatarColor(form.user_name) }">
          <span>{{ getInitials(form.user_name) }}</span>
        </div>
        <div class="uln-avatar-info">
          <div class="uln-avatar-name">{{ form.user_name || 'Nome do Usuário' }}</div>
          <div class="uln-avatar-role">{{ getRoleLabel(form.user_role) }}</div>
        </div>
      </div>

      <!-- Form -->
      <form class="uln-form" @submit.prevent="onSubmit">

        <div class="uln-form-row">
          <!-- Nome -->
          <div class="uln-field" :class="{ 'uln-field-error': errors.user_name }">
            <label class="uln-label">
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/></svg>
              Nome *
            </label>
            <input v-model="form.user_name" class="uln-input" placeholder="João" @blur="validateField('user_name')" />
            <span v-if="errors.user_name" class="uln-error-msg">{{ errors.user_name }}</span>
          </div>

          <!-- Sobrenome -->
          <div class="uln-field" :class="{ 'uln-field-error': errors.user_sobrenome }">
            <label class="uln-label">Sobrenome *</label>
            <input v-model="form.user_sobrenome" class="uln-input" placeholder="Silva" @blur="validateField('user_sobrenome')" />
            <span v-if="errors.user_sobrenome" class="uln-error-msg">{{ errors.user_sobrenome }}</span>
          </div>
        </div>

        <!-- Email -->
        <div class="uln-field" :class="{ 'uln-field-error': errors.user_email }">
          <label class="uln-label">
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" stroke-width="2"/><polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2"/></svg>
            E-mail *
          </label>
          <input v-model="form.user_email" type="email" class="uln-input" placeholder="joao@exemplo.com" @blur="validateField('user_email')" />
          <span v-if="errors.user_email" class="uln-error-msg">{{ errors.user_email }}</span>
        </div>

        <!-- Telefone -->
        <div class="uln-field" :class="{ 'uln-field-error': errors.user_phone }">
          <label class="uln-label">
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.8 8.62 19.79 19.79 0 01.7 2.05 2 2 0 012.68 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.64a16 16 0 006.29 6.29l.78-.78a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2"/></svg>
            Telefone *
          </label>
          <input v-model="form.user_phone" class="uln-input" placeholder="+55 (11) 99999-9999" @blur="validateField('user_phone')" />
          <span v-if="errors.user_phone" class="uln-error-msg">{{ errors.user_phone }}</span>
        </div>

        <!-- Cargo -->
        <div class="uln-field" :class="{ 'uln-field-error': errors.user_role }">
          <label class="uln-label">
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" stroke="currentColor" stroke-width="2"/></svg>
            Cargo / Função *
          </label>
          <div class="uln-role-select">
            <div
              v-for="opt in roleOptions"
              :key="opt.value"
              class="uln-role-opt"
              :class="{ 'uln-role-sel': form.user_role === opt.value }"
              @click="form.user_role = opt.value; errors.user_role = ''"
            >
              <div class="uln-role-icon">
                <svg v-if="opt.value === 'admin'" width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" stroke="currentColor" stroke-width="2"/></svg>
                <svg v-else width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/></svg>
              </div>
              <div>
                <div class="uln-role-name">{{ opt.label }}</div>
                <div class="uln-role-desc">{{ opt.value === 'admin' ? 'Acesso total ao sistema' : 'Acesso básico à plataforma' }}</div>
              </div>
              <div v-if="form.user_role === opt.value" class="uln-role-check">✓</div>
            </div>
          </div>
          <span v-if="errors.user_role" class="uln-error-msg">{{ errors.user_role }}</span>
        </div>

        <!-- Status (only in edit mode) -->
        <div v-if="isEditing" class="uln-field">
          <label class="uln-label">Status</label>
          <div class="uln-status-toggle">
            <button
              type="button"
              class="uln-status-opt"
              :class="{ 'uln-status-active': form.user_ativo === 1 }"
              @click="form.user_ativo = 1"
            >
              <span class="uln-status-dot uln-dot-active"></span> Ativo
            </button>
            <button
              type="button"
              class="uln-status-opt"
              :class="{ 'uln-status-inactive': form.user_ativo === 0 }"
              @click="form.user_ativo = 0"
            >
              <span class="uln-status-dot uln-dot-inactive"></span> Inativo
            </button>
          </div>
        </div>

        <!-- Password (add mode only) -->
        <div v-if="!isEditing" class="uln-field">
          <label class="uln-label">
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="2"/></svg>
            Senha inicial
          </label>
          <div class="uln-pass-wrap">
            <input
              v-model="form.user_password"
              :type="showPass ? 'text' : 'password'"
              class="uln-input"
              placeholder="Mínimo 6 caracteres"
            />
            <button type="button" class="uln-pass-toggle" @click="showPass = !showPass">
              <svg v-if="showPass" width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              <svg v-else width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
            </button>
          </div>
          <span class="uln-hint">Deixe o campo para usar a senha padrão do sistema</span>
        </div>

        <!-- Error Alert -->
        <div v-if="submitError" class="uln-alert-error">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          {{ submitError }}
        </div>

        <!-- Success Alert -->
        <div v-if="submitSuccess" class="uln-alert-success">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" stroke-width="2"/><polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          {{ isEditing ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!' }}
        </div>

        <!-- Actions -->
        <div class="uln-footer">
          <button type="button" class="uln-btn-cancel" @click="closeSidebar">Cancelar</button>
          <button type="submit" class="uln-btn-submit" :disabled="submitting">
            <span v-if="submitting" class="uln-btn-spinner"></span>
            <svg v-else-if="isEditing" width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" stroke="currentColor" stroke-width="2"/><polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" stroke-width="2"/><polyline points="7 3 7 8 15 8" stroke="currentColor" stroke-width="2"/></svg>
            <svg v-else width="15" height="15" fill="none" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" stroke-width="2"/><line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2"/></svg>
            {{ submitting ? 'Salvando…' : (isEditing ? 'Salvar alterações' : 'Criar usuário') }}
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import store from '@/store'

export default {
  name: 'UserListAddNew',
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-user-sidebar-active',
  },
  props: {
    isAddNewUserSidebarActive: { type: Boolean, required: true },
    editingUser: { type: Object, default: null },
    roleOptions: { type: Array, required: true },
    planOptions: { type: Array, required: true },
  },
  data() {
    return {
      showPass: false,
      submitting: false,
      submitError: '',
      submitSuccess: false,
      form: this.blankForm(),
      errors: {},
    }
  },
  computed: {
    isEditing() { return !!this.editingUser },
  },
  watch: {
    isAddNewUserSidebarActive(val) {
      if (val) {
        this.submitError = ''
        this.submitSuccess = false
        this.errors = {}
        this.showPass = false
        if (this.editingUser) {
          this.form = {
            user_name: this.editingUser.fullName ? this.editingUser.fullName.split(' ')[0] : '',
            user_sobrenome: this.editingUser.username || '',
            user_email: this.editingUser.email || '',
            user_phone: this.editingUser.contact || '',
            user_role: this.editingUser.role || '',
            user_ativo: this.editingUser.status === 'Ativo' ? 1 : 0,
            user_password: '',
          }
        } else {
          this.form = this.blankForm()
        }
      }
    },
  },
  methods: {
    blankForm() {
      return {
        user_name: '',
        user_sobrenome: '',
        user_email: '',
        user_phone: '',
        user_role: '',
        user_ativo: 1,
        user_password: '1234567',
        user_photo_url: '',
      }
    },
    closeSidebar() {
      this.$emit('update:is-add-new-user-sidebar-active', false)
      this.$emit('close')
    },
    validateField(field) {
      this.$set(this.errors, field, '')
      if (field === 'user_name' && !this.form.user_name.trim()) {
        this.$set(this.errors, field, 'Nome é obrigatório')
      }
      if (field === 'user_sobrenome' && !this.form.user_sobrenome.trim()) {
        this.$set(this.errors, field, 'Sobrenome é obrigatório')
      }
      if (field === 'user_email') {
        if (!this.form.user_email.trim()) {
          this.$set(this.errors, field, 'E-mail é obrigatório')
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.user_email)) {
          this.$set(this.errors, field, 'E-mail inválido')
        }
      }
      if (field === 'user_phone' && !this.form.user_phone.trim()) {
        this.$set(this.errors, field, 'Telefone é obrigatório')
      }
    },
    validateAll() {
      const fields = ['user_name', 'user_sobrenome', 'user_email', 'user_phone']
      fields.forEach(f => this.validateField(f))
      if (!this.form.user_role) this.$set(this.errors, 'user_role', 'Selecione um cargo')
      return !Object.values(this.errors).some(e => e)
    },
    async onSubmit() {
      if (!this.validateAll()) return
      this.submitting = true
      this.submitError = ''
      this.submitSuccess = false
      try {
        if (this.isEditing) {
          await store.dispatch('app-user/updatedUser', {
            id: this.editingUser.id,
            ...this.form,
          })
        } else {
          await store.dispatch('app-user/addUser', this.form)
        }
        this.submitSuccess = true
        setTimeout(() => {
          this.$emit('refetch-data')
          this.closeSidebar()
        }, 900)
      } catch (e) {
        this.submitError = 'Erro ao salvar. Tente novamente.'
      } finally {
        this.submitting = false
      }
    },
    getInitials(name) {
      if (!name || !name.trim()) return '?'
      const parts = name.trim().split(' ')
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase()
    },
    getAvatarColor(name) {
      const colors = ['#E86A10','#0D3349','#3A86FF','#8338EC','#06D6A0','#EF476F']
      if (!name) return colors[0]
      return colors[name.charCodeAt(0) % colors.length]
    },
    getRoleLabel(role) {
      if (role === 'admin') return 'Administrador'
      if (role === 'client') return 'Cliente'
      return 'Função não definida'
    },
  },
}
</script>

<style scoped>
/* ─── Overlay ─── */
.uln-overlay {
  position: fixed; inset: 0; z-index: 9998;
  background: rgba(13,51,73,0.45); backdrop-filter: blur(4px);
  display: flex; justify-content: flex-end;
}

/* ─── Sidebar ─── */
.uln-sidebar {
  width: 460px; max-width: 100vw; height: 100%;
  background: var(--dk-bg-card, #fff); overflow-y: auto; display: flex; flex-direction: column;
  box-shadow: -8px 0 40px rgba(0,0,0,0.18);
  animation: uln-slide-in 0.25s cubic-bezier(0.25,0.46,0.45,0.94);
}
@keyframes uln-slide-in {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: none; opacity: 1; }
}

/* ─── Header ─── */
.uln-header {
  display: flex; align-items: center; gap: 14px;
  padding: 24px 24px 20px; border-bottom: 1.5px solid var(--dk-border-light, #f0f2f5);
  position: sticky; top: 0; background: var(--dk-bg-card, #fff); z-index: 2;
}
.uln-header-icon {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.uln-icon-add { background: rgba(232,106,16,0.1); color: #E86A10; }
.uln-icon-edit { background: rgba(58,134,255,0.1); color: #3A86FF; }
.uln-title { font-size: 18px; font-weight: 700; color: var(--dk-text-primary, #0D3349); margin: 0; }
.uln-subtitle { font-size: 12px; color: var(--dk-text-muted, #8a94a6); margin: 2px 0 0; }
.uln-close {
  margin-left: auto; background: none; border: none; cursor: pointer;
  color: var(--dk-text-faint, #aab0bc); padding: 6px; border-radius: 8px; transition: all 0.15s;
  display: flex; align-items: center;
}
.uln-close:hover { background: #fee2e2; color: #ea5455; }

/* ─── Avatar Section ─── */
.uln-avatar-section {
  display: flex; align-items: center; gap: 16px;
  padding: 20px 24px; background: var(--dk-bg-card-hover, #fafbfc); border-bottom: 1.5px solid #f0f2f5;
}
.uln-avatar-preview {
  width: 56px; height: 56px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; font-weight: 700; color: #fff; flex-shrink: 0;
  transition: background 0.3s;
}
.uln-avatar-name { font-size: 15px; font-weight: 600; color: var(--dk-text-primary, #0D3349); }
.uln-avatar-role { font-size: 12px; color: var(--dk-text-muted, #8a94a6); margin-top: 2px; }

/* ─── Form ─── */
.uln-form { flex: 1; padding: 24px; display: flex; flex-direction: column; gap: 18px; }
.uln-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.uln-field { display: flex; flex-direction: column; gap: 6px; }
.uln-field-error .uln-input { border-color: #ea5455; }
.uln-label {
  font-size: 12px; font-weight: 600; color: var(--dk-text-secondary, #5a6a7a);
  display: flex; align-items: center; gap: 5px;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.uln-input {
  padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid var(--dk-border-input, #e8ecf0); font-size: 14px;
  color: var(--dk-text-primary, #344563); background: var(--dk-bg-card-hover, #fafbfc);
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%; box-sizing: border-box;
}
.uln-input:focus { outline: none; border-color: #E86A10; background: var(--dk-bg-card, #fff); box-shadow: 0 0 0 3px rgba(232,106,16,0.1); }
.uln-error-msg { font-size: 11.5px; color: #ea5455; font-weight: 500; }
.uln-hint { font-size: 11.5px; color: var(--dk-text-faint, #aab0bc); }

/* ─── Password ─── */
.uln-pass-wrap { position: relative; display: flex; }
.uln-pass-wrap .uln-input { padding-right: 40px; }
.uln-pass-toggle {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--dk-text-faint, #aab0bc);
  display: flex; align-items: center;
}
.uln-pass-toggle:hover { color: #E86A10; }

/* ─── Role Select ─── */
.uln-role-select { display: flex; flex-direction: column; gap: 10px; }
.uln-role-opt {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 10px; border: 1.5px solid var(--dk-border-input, #e8ecf0);
  cursor: pointer; transition: all 0.15s; background: var(--dk-bg-card-hover, #fafbfc);
}
.uln-role-opt:hover { border-color: #E86A10; background: var(--dk-bg-card, #fff); }
.uln-role-sel { border-color: #E86A10 !important; background: rgba(232,106,16,0.04) !important; }
.uln-role-icon {
  width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
  background: rgba(232,106,16,0.1); color: #E86A10;
  display: flex; align-items: center; justify-content: center;
}
.uln-role-name { font-size: 13.5px; font-weight: 600; color: var(--dk-text-primary, #344563); }
.uln-role-desc { font-size: 11.5px; color: var(--dk-text-faint, #aab0bc); margin-top: 1px; }
.uln-role-check {
  margin-left: auto; width: 24px; height: 24px; border-radius: 50%;
  background: #E86A10; color: #fff; display: flex;
  align-items: center; justify-content: center; font-size: 12px; font-weight: 700;
  flex-shrink: 0;
}

/* ─── Status Toggle ─── */
.uln-status-toggle { display: flex; gap: 10px; }
.uln-status-opt {
  flex: 1; padding: 10px; border-radius: 10px; border: 1.5px solid var(--dk-border-input, #e8ecf0);
  background: var(--dk-bg-card-hover, #fafbfc); cursor: pointer; font-size: 13px; font-weight: 600;
  color: var(--dk-text-secondary, #6e7d8f); display: flex; align-items: center; justify-content: center;
  gap: 8px; transition: all 0.15s;
}
.uln-status-dot { width: 8px; height: 8px; border-radius: 50%; }
.uln-dot-active { background: #28c76f; }
.uln-dot-inactive { background: #82868b; }
.uln-status-active { border-color: #28c76f !important; background: rgba(40,199,111,0.06) !important; color: #28c76f !important; }
.uln-status-inactive { border-color: #82868b !important; background: rgba(130,134,139,0.06) !important; color: #82868b !important; }

/* ─── Alerts ─── */
.uln-alert-error, .uln-alert-success {
  padding: 12px 16px; border-radius: 10px; font-size: 13px; font-weight: 500;
  display: flex; align-items: center; gap: 10px;
}
.uln-alert-error { background: rgba(234,84,85,0.08); color: #ea5455; border: 1px solid rgba(234,84,85,0.2); }
.uln-alert-success { background: rgba(40,199,111,0.08); color: #28c76f; border: 1px solid rgba(40,199,111,0.2); }

/* ─── Footer ─── */
.uln-footer {
  display: flex; gap: 12px;
  padding: 20px 24px; border-top: 1.5px solid var(--dk-border-light, #f0f2f5);
  position: sticky; bottom: 0; background: var(--dk-bg-card, #fff);
}
.uln-btn-cancel {
  flex: 1; padding: 12px; border-radius: 10px;
  border: 1.5px solid #e8ecf0; background: var(--dk-bg-card, #fff);
  font-size: 14px; font-weight: 600; color: var(--dk-text-secondary, #6e7d8f); cursor: pointer;
  transition: all 0.15s;
}
.uln-btn-cancel:hover { border-color: #0D3349; color: var(--dk-text-primary, #0D3349); }
.uln-btn-submit {
  flex: 2; padding: 12px; border-radius: 10px; border: none;
  background: #E86A10; color: #fff; font-size: 14px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: background 0.15s; box-shadow: 0 4px 12px rgba(232,106,16,0.3);
}
.uln-btn-submit:hover:not(:disabled) { background: #cf5e0d; }
.uln-btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
.uln-btn-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4); border-top-color: var(--dk-bg-card, #fff);
  animation: uln-spin 0.6s linear infinite;
}
@keyframes uln-spin { to { transform: rotate(360deg); } }

@media (max-width: 480px) {
  .uln-sidebar { width: 100vw; }
  .uln-form-row { grid-template-columns: 1fr; }
}
</style>
